import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Section, EyebrowHeading } from "@/components/site/Section";

import { TrustBand } from "@/components/site/TrustBand";
import { RoiCalculator } from "@/components/site/hub/RoiCalculator";

import { guides as htmlGuides } from "@/content/guides";
import { LeadMagnetForm } from "@/components/site/LeadMagnetForm";
import { Download, FileText, Calculator, BookOpen, ArrowRight, Sparkles, Bell, X } from "lucide-react";
import { EditorialHero } from "@/components/site/wow/EditorialHero";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { aeoGeo } from "@/lib/seo/schema";

export const Route = createFileRoute("/guider")({
  head: () => ({
    meta: [
      { title: "Guider & verktyg — Praktiska PDF:er för filmteam | Fenny" },
      { name: "description", content: "Guider och verktyg för filmproduktion: formatguide, briefmall och ROI-kalkylator. Läs online eller hämta en PDF." },
      { property: "og:title", content: "Guider & verktyg | Fenny" },
      { property: "og:description", content: "Mallar, kalkylatorer och guider för team som jobbar med rörligt. Läs online — PDF mailas på begäran." },

      { property: "og:url", content: "https://fenny.se/guider" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/guider" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/guider", name: "Guider & verktyg — Praktiska PDF:er för filmteam | Fenny", description: "ROI-kalkylator, formatguide och mallar för team som jobbar med film. Läs gratis online — eller få PDF:en mejlad för utskrift och intern delning."})) },
    ],
  }),
  component: GuiderPage,
});

const types = [
  { icon: FileText, label: "PDF-guider" },
  { icon: Calculator, label: "Kalkylatorer" },
  { icon: BookOpen, label: "Mallar" },
  { icon: Sparkles, label: "Kommande" },
];

type GuideCard = {
  slug: string;
  eyebrow: string;
  title: string;
  desc: string;
  cta: string;
  type: string;
  pages: string;
  mode: "download" | "notify";
};

const guideCards: GuideCard[] = [
  {
    slug: "formatguide-2026",
    eyebrow: "Mest hämtad",
    title: "Formatguide 2026",
    desc: "Vilket filmformat funkar var? En lathund för feed, story, YouTube, webinars och paid social — inklusive längder, hooks och CTA-mönster.",
    cta: "Ladda ner PDF",
    type: "PDF",
    pages: "24 sidor",
    mode: "download",
  },
  {
    slug: "brief-mall",
    eyebrow: "Mall",
    title: "Brief som inte slösar tid",
    desc: "En 1-sidig brief-mall som hjälper er säga rätt sak innan ett första produktionsmöte. Använd den med oss eller med vem som helst.",
    cta: "Ladda ner PDF",
    type: "PDF",
    pages: "1 sida",
    mode: "download",
  },
  {
    slug: "sociala-hooks-2026",
    eyebrow: "Lathund",
    title: "Sociala hooks som funkar 2026",
    desc: "10 färska hook-formler som drar tittarna förbi 3-sekundersgränsen — med svenska exempel, mätbar effekt och de vanligaste fallgroparna.",
    cta: "Ladda ner PDF",
    type: "PDF",
    pages: "12 sektioner",
    mode: "download",
  },
  {
    slug: "manadsvis-filmplan",
    eyebrow: "Mall",
    title: "Månadsvis filmplan",
    desc: "Strukturen 'ankare + ekon' — så planerar du in månadens filmer per kanal, format och mål. Samma logik vi själva använder med Pulse-kunder.",
    cta: "Ladda ner PDF",
    type: "PDF",
    pages: "5 sektioner",
    mode: "download",
  },
];

function GuideCardItem({ g }: { g: GuideCard }) {
  const [open, setOpen] = useState(false);
  const isDownload = g.mode === "download";

  return (
    <article
      className={`group flex flex-col rounded-3xl border p-8 md:p-10 transition-all ${
        isDownload
          ? "border-mint/30 bg-mint/[0.04] hover:bg-mint/[0.08]"
          : "border-dashed border-white/10 bg-surface/40"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-mint">{g.eyebrow}</div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{g.type} · {g.pages}</div>
      </div>
      <h3 className="mt-3 text-2xl font-semibold md:text-3xl">{g.title}</h3>
      <p className="mt-4 text-sm leading-relaxed text-foreground/85">{g.desc}</p>
      <div className="mt-8 flex-1" />
      {open ? (
        <div className="mt-2">
          <LeadMagnetForm
            magnetSlug={g.slug}
            mode={g.mode}
            title={g.title}
            location={`guider_library:${g.slug}`}
            variant="inline"
            compact
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" /> Stäng
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`inline-flex items-center gap-2 self-start rounded-full px-5 py-2.5 text-sm font-medium transition-transform hover:-translate-y-0.5 ${
            isDownload
              ? "bg-foreground text-background"
              : "border border-white/15 bg-surface/60 text-foreground"
          }`}
        >
          {isDownload ? <Download className="h-4 w-4" /> : <Bell className="h-4 w-4 text-mint" />}
          {g.cta}
        </button>
      )}
    </article>
  );
}

function GuiderPage() {
  return (
    <div>
      <EditorialHero
        eyebrow="Guider & verktyg"
        title="Verktyg vi skulle önskat själva."
        highlight={[2, 3]}
        tone="mint"
        lede="Praktiska resurser för team som jobbar med film. Börja med kalkylatorn nedan: tre val och vi pekar ut rätt nivå för er — utan att ni behöver maila någon."
      />

      <Section className="pt-0">
        <div className="flex flex-wrap gap-2">
          {types.map((t) => (
            <span key={t.label} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-surface/40 px-4 py-2 text-xs text-muted-foreground">
              <t.icon className="h-3.5 w-3.5 text-mint" /> {t.label}
            </span>
          ))}
        </div>
      </Section>

      {/* ROI calculator */}
      <Section className="pt-0">
        <div className="mb-10">
          <EyebrowHeading
            eyebrow="Hjälp på 30 sekunder"
            title="ROI-kalkylator för rörligt"
            lede="Tre val. Vi pekar ut rätt Pulse-nivå utifrån era mål, kanaler och publiceringstakt — utan att ni behöver maila någon."
          />
        </div>
        <RoiCalculator />
      </Section>

      {/* Guide library */}
      <Section className="bg-surface/40">
        <EyebrowHeading
          eyebrow="Biblioteket"
          title="Färdiga guider och mallar"
          lede="Lämna mejl, få PDF:en direkt i inbox — och först i kön när nästa släpps. Inget nyhetsbrev, ingen spam."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {guideCards.map((g) => (
            <GuideCardItem key={g.slug} g={g} />
          ))}
        </div>
      </Section>

      {/* HTML guides */}
      <Section>
        <EyebrowHeading
          eyebrow="Läs direkt"
          title="Guider att läsa på 5–10 min"
          lede="Färdiga HTML-versioner — ingen nedladdning, ingen email-gate."
        />
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {htmlGuides.map((g) => (
            <Link
              key={g.slug}
              to="/guider/$slug"
              params={{ slug: g.slug }}
              className="group flex flex-col rounded-3xl border border-white/10 bg-surface/40 p-6 transition-all hover:border-mint/40 hover:bg-mint/[0.05]"
            >
              <div className="text-xs uppercase tracking-widest text-mint">{g.type} · {g.readMinutes} min</div>
              <h3 className="mt-3 text-xl font-semibold leading-tight">{g.title}</h3>
              <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{g.subtitle}</p>
              <span className="mt-6 inline-flex items-center gap-1 text-sm text-mint">
                Läs guiden <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
      </Section>

      {/* Brevet */}
      <SignatureCta
        eyebrow="Vill ha mer?"
        title="Gratis genomgång av er filmplan."
        body="30 minuter där vi tittar på det ni gör nu och ger konkreta idéer på vad som kan justeras."
        tone="mint"
      />

      <TrustBand ctaLocation="guider_trust_band" />
    </div>
  );
}
